import { api } from '@verifime/api-definition';
import { useCallback, useEffect, useState } from 'react';
import { TNotificationHandler } from '../../../../types';

export interface UseOrganisationCodeReturnPathResult {
  selectedCode: string;
  setSelectedCode: (code: string) => void;
  isLoading: boolean;
  organisationCodes: string[];
  initialReturnUrl: string;
  handleSave: (code: string, url: string) => Promise<void>;
}

export function useOrganisationCodeReturnPath(
  organisationId: string,
  successNotificationHandler: TNotificationHandler = console.log,
  errorNotificationHandler: TNotificationHandler = console.error,
): UseOrganisationCodeReturnPathResult {
  const [selectedCode, setSelectedCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [organisationCodes, setOrganisationCodes] = useState<string[]>([]);
  const [returnUrlMap, setReturnUrlMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    setIsLoading(true);
    api
      .getV1organisationOrganisationIdcode({
        params: { organisationId },
      })
      .then((data) => {
        const codes = data.map((item) => item.code).filter((code) => !!code);
        const urlMapping = new Map<string, string>();

        data.forEach((item) => {
          const returnUrl = item.returnUrl || '';
          if (item.code) {
            urlMapping.set(item.code, returnUrl);
          }
        });

        setOrganisationCodes(codes);
        setReturnUrlMap(urlMapping);

        // Set the first code as default selection
        if (codes.length > 0) {
          setSelectedCode(codes[0]);
        }
      })
      .catch((err) => {
        errorNotificationHandler(
          err instanceof Error ? err.message : 'Failed to fetch organisation codes',
        );
        console.error('Error fetching organisation codes:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [organisationId, errorNotificationHandler]);

  const handleSave = useCallback(
    async (code: string, url: string) => {
      const currentUrl = returnUrlMap.get(code);
      // Don't do anything if the URL hasn't changed
      if (url === currentUrl) {
        return;
      }

      try {
        await api.putV1organisationcodeOrganisationCodereturnUrl(
          { returnUrl: url || null },
          { params: { organisationCode: code } },
        );
        setReturnUrlMap((prev) => new Map(prev).set(code, url));

        successNotificationHandler('Return URL updated successfully');
      } catch (err) {
        errorNotificationHandler(
          err instanceof Error ? err.message : 'Failed to update return URL',
        );
        console.error('Error updating return URL:', err);
      }
    },
    [returnUrlMap, successNotificationHandler, errorNotificationHandler],
  );

  const initialReturnUrl = returnUrlMap.get(selectedCode) || '';

  return {
    selectedCode,
    setSelectedCode,
    isLoading,
    organisationCodes,
    initialReturnUrl,
    handleSave,
  };
}
